/**
 * FCM (Firebase Cloud Messaging) client.
 *
 * Gracefully no-ops when env vars are missing — the app works normally
 * (notification bell via REST polling) without any push configuration.
 *
 * Required env vars:
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID
 *   VITE_FIREBASE_STORAGE_BUCKET
 *   VITE_FIREBASE_MESSAGING_SENDER_ID
 *   VITE_FIREBASE_APP_ID
 *   VITE_FIREBASE_VAPID_KEY
 */

import { api } from './api';
import { notify } from './toast';
import type { AppNotification } from './api';

// Lazy import to avoid bundling Firebase when env is not configured
let fcmInitialized = false;
let registeredToken: string | null = null;

function isFcmConfigured(): boolean {
  return !!(
    import.meta.env.VITE_FIREBASE_API_KEY &&
    import.meta.env.VITE_FIREBASE_PROJECT_ID &&
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID &&
    import.meta.env.VITE_FIREBASE_APP_ID &&
    import.meta.env.VITE_FIREBASE_VAPID_KEY
  );
}

/**
 * Initialize FCM: request permission, get token, register with backend,
 * and listen for foreground messages.
 *
 * @param onNotification - callback to handle incoming foreground notifications
 */
export async function initFcm(
  onNotification: (notification: AppNotification) => void
): Promise<void> {
  if (!isFcmConfigured()) {
    // No-op: FCM env vars not set, REST polling will handle unread count
    return;
  }

  if (fcmInitialized) return;

  try {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getMessaging, getToken, onMessage, isSupported } = await import('firebase/messaging');

    const supported = await isSupported();
    if (!supported) return;

    const firebaseConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    };

    // Avoid duplicate app initialization
    const app =
      getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
    const messaging = getMessaging(app);

    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return;
    }

    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    });

    if (!token) return;

    registeredToken = token;

    // Register token with backend
    await api.registerFcmToken(token);

    // Listen for foreground messages
    onMessage(messaging, (payload) => {
      const { notification, data } = payload;

      const appNotification: AppNotification = {
        id: data?.id ?? crypto.randomUUID(),
        type: (data?.type as AppNotification['type']) ?? 'system',
        title: notification?.title ?? 'Nova notificação',
        body: notification?.body ?? '',
        data: {
          conversationId: data?.conversationId,
          projectId: data?.projectId,
          chapterId: data?.chapterId,
          userId: data?.userId,
          url: data?.url,
        },
        read: false,
        createdAt: new Date().toISOString(),
      };

      onNotification(appNotification);

      // Show a toast for foreground notifications
      notify.info(appNotification.title, appNotification.body);
    });

    fcmInitialized = true;
  } catch (error) {
    // Non-fatal: FCM failure should never break the app
    console.warn('FCM initialization failed:', error);
  }
}

/**
 * Unregister the FCM token from the backend (e.g., on logout).
 */
export async function cleanupFcm(): Promise<void> {
  if (!registeredToken) return;
  try {
    await api.unregisterFcmToken(registeredToken);
    registeredToken = null;
    fcmInitialized = false;
  } catch (error) {
    console.warn('FCM cleanup failed:', error);
  }
}
